/**
 * All German UI texts in one place. There is only German for now, but keeping
 * the strings out of components means a second language is a file, not a
 * refactoring. Keys are English and describe the place, not the wording.
 */
import type { DamageCategory, DamageStatus, MemberRole } from '../status';
import type { AircraftView } from '../geometry';
import type { NoticeLevel } from '../notices';
import { MAX_ADDITIONAL_EMAILS } from '../limits';

export const statusLabel: Readonly<Record<DamageStatus, string>> = {
  gemeldet: 'Gemeldet',
  bekannt: 'Bekannt',
  beobachtung: 'In Beobachtung',
  // Owner, 08.10.2026: pilots never see a repair under way (the aircraft is not available then), so the label says what they can see.
  in_reparatur: 'Reparatur geplant',
  akzeptiert: 'Akzeptiert',
  behoben: 'Behoben',
  verworfen: 'Verworfen',
};

export const categoryLabel: Readonly<Record<DamageCategory, string>> = {
  lack: 'Lackschaden',
  kratzer: 'Kratzer',
  beule: 'Beule',
  riss: 'Riss',
  sonstiges: 'Sonstiges',
};

export const viewLabel: Readonly<Record<AircraftView, string>> = {
  oben: 'Oben',
  unten: 'Unten',
  links: 'Links',
  rechts: 'Rechts',
  vorne: 'Vorne',
  hinten: 'Hinten',
  innen: 'Innen',
};

export const roleLabel: Readonly<Record<MemberRole, string>> = {
  member: 'Mitglied',
  editor: 'Technik',
  admin: 'Administrator',
  inactive: 'Inaktiv',
};

/** Levels of a notice of the technicians (notices.ts); the badge on the aircraft. */
export const noticeLevelLabel: Readonly<Record<NoticeLevel, string>> = {
  info: 'Info',
  einschraenkung: 'Einschränkung',
  gesperrt: 'Gesperrt',
};

export const de = {
  app: {
    name: 'Außencheck',
    /** Shown in the sidebar outside a club; inside a club the club's code stands there. */
    mark: 'AC',
  },
  auth: {
    emailLabel: 'E-Mail-Adresse',
    requestCode: 'Code anfordern',
    codeSentGeneric: 'Wenn diese Adresse hinterlegt ist, ist der Code unterwegs.',
    codeLabel: 'Anmeldecode',
    verify: 'Anmelden',
    resendIn: (seconds: number) => `Code erneut senden in ${seconds} s`,
    resend: 'Code erneut senden',
    invalidCode: 'Code ist ungültig oder abgelaufen.',
    tooManyAttempts: 'Zu viele Versuche. Bitte einen neuen Code anfordern.',
    rateLimited: (minutes: number) => `Bitte in ${minutes} Minuten erneut versuchen.`,
    signOut: 'Auf diesem Gerät abmelden',
    signOutEverywhere: 'Auf allen Geräten abmelden',
    signOutEverywhereHint:
      'Beendet deine Anmeldung auch auf anderen Handys und Browsern – z. B. wenn ein Gerät verloren ging.',
    signOutEverywhereConfirm: 'Auf allen Geräten abmelden?',
    signOutEverywhereConfirmBody:
      'Du wirst auf diesem Gerät, auf anderen Handys und in jedem Browser abgemeldet. Benachrichtigungen kommen auf keinem davon mehr an, bis du dich dort wieder anmeldest.',
    notSignedIn: 'Nicht angemeldet',
    forbidden: 'Keine Berechtigung',
  },
  nav: {
    fleet: 'Flotte',
    search: 'Suche',
    inbox: 'Eingang',
    profile: 'Profil',
    admin: 'Verwaltung',
  },
  damage: {
    report: 'Schaden melden',
    create: 'Schaden anlegen',
    edit: 'Bearbeiten',
    title: 'Titel',
    description: 'Beschreibung',
    category: 'Kategorie',
    status: 'Status',
    photos: 'Fotos',
    overviewPhoto: 'Übersichtsfoto (1–2 m Abstand)',
    detailPhoto: 'Detailfoto',
    nearbyWarning: (n: number) =>
      n === 1
        ? 'In der Nähe ist bereits 1 Schaden erfasst.'
        : `In der Nähe sind bereits ${n} Schäden erfasst.`,
    sameDamage: 'Ist derselbe Schaden',
    history: 'Verlauf',
    comments: 'Rückfragen/Hinweise',
    reportedBy: 'Gemeldet von',
    reportedAt: 'Gemeldet am',
    deleted: 'Gelöscht',
    restore: 'Wiederherstellen',
    undo: 'Rückgängig',
  },
  review: {
    take: 'Übernehmen',
    confirm: 'Bestätigen',
    merge: 'Zusammenführen',
    reject: 'Verwerfen',
    accept: 'Akzeptieren',
    ask: 'Rückfrage/Hinweis',
    reasonLabel: 'Begründung (wird dem Melder mitgeteilt)',
    reasonTooShort: 'Bitte eine Begründung eingeben.',
    overdue: 'Sichtung überfällig',
  },
  admin: {
    members: 'Mitglieder',
    fleet: 'Flotte',
    protocol: 'Protokoll',
    trash: 'Papierkorb',
    settings: 'Einstellungen',
    csvImport: 'CSV importieren',
    invite: 'Einladen',
    lastAdmin: 'Der letzte Administrator kann nicht entfernt werden.',
  },
  /**
   * Refusals of the fleet routes, word for word the same from the API and the
   * demo store (docs/fleet-admin-ui.md, section 4).
   */
  fleet: {
    hasDamages: 'Flugzeug hat Schäden – stattdessen außer Dienst stellen',
    registrationLocked:
      'Das Kennzeichen lässt sich nicht mehr ändern: zu diesem Flugzeug gibt es schon Schäden.',
    registrationTaken: 'Dieses Kennzeichen ist im Verein schon eingetragen.',
    registrationInTrash:
      'Dieses Kennzeichen ist im Verein schon eingetragen (gelöscht) – stattdessen wiederherstellen.',
    registrationInDepartment: (area: string) =>
      `Dieses Kennzeichen ist schon im Bereich „${area}“ eures Vereins eingetragen. Ein Flugzeug gehört in genau einen Bereich – dort erst löschen.`,
    registrationFormat: 'Kennzeichen im Format D-ABCD oder D-1234 angeben.',
    codeTaken: 'Dieses Kürzel ist schon vergeben (Katalog oder eigenes Muster).',
    codeFormat: 'Kürzel: 2 bis 16 Zeichen, Buchstaben, Ziffern und Bindestrich (z. B. ASK21).',
    codeFixed: 'Das Kürzel eines Musters lässt sich nicht ändern.',
    catalogueType: 'Muster aus dem Katalog lassen sich nicht ändern.',
    bundledDrawings:
      'Die Zeichnungen dieses Musters sind fest hinterlegt (Flughandbuch) und lassen sich nicht ersetzen.',
    unknownType: 'Unbekanntes Muster.',
    notDeleted: 'Das Flugzeug ist nicht gelöscht.',
    textLength: (what: string, min: number, max: number) =>
      `${what} braucht ${min} bis ${max} Zeichen.`,
    sortOrderRange: (max: number) => `Reihenfolge muss eine ganze Zahl von 0 bis ${max} sein.`,
    noChange: 'Keine Änderung angegeben.',
  },
  /**
   * The class of an aircraft type (0025, `aircraftClass.ts`): what the monthly
   * fee counts. The refusal is the same from the API and the demo store.
   */
  aircraftClass: {
    label: {
      motor: 'Motorflugzeug',
      tmg: 'Motorsegler',
      glider: 'Segelflugzeug',
      ul: 'UL',
    },
    field: 'Klasse',
    choose: 'Klasse wählen …',
    hint: 'Bestimmt die Monatsgebühr: gezählt werden die aktiven Flugzeuge des Vereins am 1. jedes Monats, je Klasse.',
    required: 'Bitte die Klasse des Musters angeben (Motorflugzeug, Motorsegler, Segelflugzeug oder UL).',
    unknown: 'Unbekannte Klasse.',
    missing: 'Klasse fehlt',
    missingPrompt: (n: number) =>
      n === 1
        ? 'Bei einem eigenen Muster fehlt noch die Klasse. Sie bestimmt die Monatsgebühr – bitte im Muster nachtragen.'
        : `Bei ${n} eigenen Mustern fehlt noch die Klasse. Sie bestimmt die Monatsgebühr – bitte im jeweiligen Muster nachtragen.`,
  },
  /** `GET platform/fleet-counts?month=YYYY-MM` (0025). */
  fleetCounts: {
    badMonth: 'Monat im Format JJJJ-MM angeben (z. B. 2026-11).',
    futureMonth: 'Der 1. dieses Monats liegt noch in der Zukunft.',
  },
  /**
   * The platform's monthly billing (`billing.ts`, GET platform/billing): the
   * screen „Abrechnung“ and the CSV for the accounting.
   */
  billing: {
    nav: 'Abrechnung',
    title: 'Abrechnung',
    intro:
      'Gezählt werden die aktiven Flugzeuge am 1. des Monats, 0:00 Uhr deutscher Zeit, je Klasse; jährlich Zahlende am 1. des ersten Monats ihres Vertragsjahres. Alle Beträge brutto inkl. USt; netto und USt je Rechnung gerundet.',
    month: 'Monat',
    due: 'Fällig in diesem Monat',
    gross: 'Brutto',
    net: 'Netto',
    vat: 'USt',
    count: (due: number, billable: number) => `${due} ${due === 1 ? 'Rechnung' : 'Rechnungen'} · ${billable} laufende ${billable === 1 ? 'Vertrag' : 'Verträge'}`,
    flagged: (n: number) => `${n} ${n === 1 ? 'Zeile' : 'Zeilen'} mit Hinweis – vor dem Rechnungsstellen prüfen.`,
    csv: 'CSV herunterladen',
    club: 'Verein',
    contract: 'Vertrag',
    list: 'Listenpreis',
    capped: 'Deckel',
    discounts: 'Nachlässe',
    monthly: 'Monatsgebühr',
    dueShort: 'Fällig',
    perMonth: '/ Monat',
    founder: (rank: number | null) => (rank ? `Pionier ${rank}` : 'Pionier'),
    founderDiscount: 'Pionierrabatt',
    yearlyDiscount: 'Jahresnachlass',
    interval: { monthly: 'monatlich', yearly: 'jährlich' } as const,
    yearlyDue: 'Jahresrechnung (12 Monate)',
    yearlyPaid: (start: string) => `jährlich bezahlt, Vertragsjahr ab ${start}`,
    countedOn: (day: string) => `gezählt am ${day}`,
    noAircraft: 'keine Flugzeuge',
    /** Column heads; the full class name is the cell's title. */
    classShort: { motor: 'Motor', tmg: 'TMG', glider: 'Segel', ul: 'UL' } as const,
    missingShort: 'o. Kl.',
    reason: {
      not_concluded: 'nicht geschlossen',
      no_start: 'Vertragsbeginn fehlt',
      not_started: 'beginnt später',
      ended: 'beendet',
      no_contract: 'kein Vertrag',
    } as const,
    flag: {
      class_missing: 'Klasse fehlt',
      duplicate_registration: 'Kennzeichen doppelt',
      contract_ends: 'Vertrag endet',
      no_contract: 'kein Vertrag',
      club_blocked: 'Verein gesperrt',
    } as const,
    classMissing: (n: number, codes: string) =>
      `${n} ${n === 1 ? 'Flugzeug' : 'Flugzeuge'} ohne Klasse (${codes}) nicht berechnet – Klasse im Verein nachtragen lassen, dann neu laden.`,
    duplicates: (n: number) =>
      `${n} ${n === 1 ? 'Kennzeichen ist' : 'Kennzeichen sind'} in mehreren Abteilungen eingetragen und nur einmal berechnet – im Verein bereinigen lassen.`,
    departments: (names: string) => `mit ${names}`,
    endsOn: (day: string) => `Vertrag endet am ${day}`,
    startsOn: (day: string) => `beginnt am ${day}`,
    openContract: 'Vertrag öffnen',
    billingLine: 'Abrechnung dieses Monats',
    empty: 'Noch keine Vereine.',
    sum: 'Summe',
  },
  /**
   * Member participation per club (setting `member_participation`): the admin's
   * choice, the hints the clients show, and the 403 texts — word for word the
   * same from the API and the demo store.
   */
  media: {
    requiredTitle: 'Foto-Pflicht',
    requiredLabel: 'Meldungen von Mitgliedern brauchen ein Foto oder Video',
    requiredHint: 'Eingeschaltet lässt sich eine Meldung erst absenden, wenn mindestens ein Foto oder Video dabei ist. Technik und Administratoren sind ausgenommen.',
    required: 'In diesem Verein gehört zu jeder Meldung mindestens ein Foto oder Video.',
    pending: 'Noch kein Foto',
  },
  participation: {
    title: 'Mitwirkung der Mitglieder',
    intro:
      'Was Mitglieder mit der Rolle „Mitglied“ schreiben dürfen. Technik und Administratoren sind nie eingeschränkt.',
    level: {
      report: 'Melden, Rückfragen/Hinweise',
      comment: 'Nur Rückfragen/Hinweise',
      read: 'Nur lesen',
    },
    explain: {
      report:
        'Mitglieder melden Schäden, ergänzen Fotos zu ihren noch ungesichteten Meldungen und schreiben Rückfragen/Hinweise.',
      comment:
        'Mitglieder sehen alles und schreiben Rückfragen/Hinweise, melden aber keine Schäden – die trägt die Technik ein.',
      read: 'Mitglieder sehen alle Schäden, schreiben aber nichts.',
    },
    reportOff: 'In diesem Verein melden nur Technik und Verwaltung Schäden.',
    commentOff: 'In diesem Verein schreiben nur Technik und Verwaltung Rückfragen/Hinweise.',
    invalid: 'Unbekannte Stufe: erlaubt sind report, comment und read.',
  },
  /** Refusals of POST manage/members/{id}/anonymize (409), same in API and demo. */
  anonymize: {
    already_anonymized: 'Diese Person ist bereits anonymisiert.',
    other_membership:
      'Diese Person ist noch in einem anderen Verein aktiv und kann hier nicht anonymisiert werden.',
    last_admin: 'Der letzte Administrator kann nicht anonymisiert werden.',
    platform_manager: 'Diese Person betreut die Plattform und kann nicht anonymisiert werden.',
  },
  /** Refusals of „Adresse ändern“ — the API and the demo store answer with these words. */
  emailChange: {
    anonymized: 'Diese Person ist anonymisiert; ihre Adresse lässt sich nicht mehr ändern.',
    other_membership:
      'Die Adresse gilt auch in einem anderen Verein dieser Person und kann hier nicht geändert werden.',
    platform_manager: 'Diese Person betreut die Plattform; ihre Adresse kann hier nicht geändert werden.',
    taken: 'Unter dieser Adresse gibt es schon eine andere Person.',
    unchanged: 'Die Adresse ist unverändert.',
    own_further: 'Das ist schon eine weitere Adresse dieser Person. Entferne sie dort zuerst.',
  },
  /**
   * Refusals of „Weitere Adresse“ (0023): a club admin adding or removing one
   * (409), and a person adding their own (only `own` and `limit` before the
   * code — anything else about another person's address would be an oracle —
   * and `taken` after a correct code, when the mailbox is proven). API and
   * demo store answer with these words.
   */
  additionalEmail: {
    anonymized: 'Diese Person ist anonymisiert; ihre Adressen lassen sich nicht mehr ändern.',
    other_membership:
      'Diese Person gehört auch zu einem anderen Verein. Weitere Adressen trägt sie dann selbst in ihrem Profil ein.',
    platform_manager: 'Bei der Plattformbetreuung und mit ihren Adressen geht das hier nicht.',
    taken: 'Unter dieser Adresse gibt es schon eine andere Person.',
    own: 'Diese Adresse ist schon hinterlegt.',
    limit: `Mehr als ${MAX_ADDITIONAL_EMAILS} weitere Adressen sind nicht möglich.`,
    noPending: 'Es wartet keine Adresse auf Bestätigung. Bitte die Adresse noch einmal eingeben.',
  },
  /**
   * A person is platform-wide; a club admin acts only on the membership in
   * their club (security review 15.10.2026): no renaming and no „überall
   * abmelden“ of somebody who is also in another club.
   */
  foreignPerson: {
    rename: 'Der Name gilt auch in einem anderen Verein dieser Person und kann hier nicht geändert werden.',
    endSessions: 'Diese Person ist auch in einem anderen Verein aktiv. „Sitzungen beenden“ würde sie überall abmelden – beende stattdessen hier die Mitgliedschaft.',
  },
  /**
   * Push notifications (API → APNs/FCM). Short on purpose: a lock screen shows
   * them to whoever holds the phone, so no names and no addresses — the
   * registration and the reporter's own title, nothing else.
   */
  push: {
    newReportTitle: (registration: string) => `Neue Meldung · ${registration}`,
    ownReportTitle: (registration: string) => `Deine Meldung · ${registration}`,
    statusBody: (status: string) => `Status: ${status}`,
    mergedBody: (ref: string) => `Zusammengeführt mit ${ref}`,
    rejectedBody: (reason: string) => `Verworfen: ${reason}`,
    commentBody: 'Neue Rückfrage/neuer Hinweis',
    commentTitle: (registration: string) => `Rückfrage/Hinweis · ${registration}`,
    /** Android notification channels, as the system settings list them. */
    channelNewReports: 'Neue Meldungen',
    channelOwnReports: 'Eigene Meldungen',
  },
  /** Profile: which notifications this person wants in this club (same API for web and app). */
  notifications: {
    title: 'Benachrichtigungen',
    newReports: 'Neue Meldungen',
    newReportsHint: 'Jede neue Schadensmeldung im Verein und jede neue Rückfrage/jeder neue Hinweis dazu.',
    ownReports: 'Eigene Meldungen',
    ownReportsHint: 'Statusänderungen und Rückfragen/Hinweise zu Schäden, die du gemeldet hast.',
    appOnly: 'Benachrichtigungen kommen in der App für iOS und Android an.',
  },
  /** Profile pictures (0014): labels and the API's refusals, word for word in the demo too. */
  welcome: {
    title: 'Willkommen bei Außencheck',
    body: 'Möchtest du die App erst einmal in Ruhe ausprobieren? Ein Rundgang zeigt dir an einem Beispielverein Schritt für Schritt, wie du einen Schaden meldest und was danach damit passiert. Was du dort tust, bleibt auf deinem Gerät – die Daten deines Vereins berührt der Rundgang nicht.',
    role: (role: string) => `Du siehst ihn als ${role}, so wie du in deinem Verein eingetragen bist.`,
    later: 'Den Rundgang findest du auch später jederzeit im Profil.',
    start: 'Rundgang starten',
    skip: 'Überspringen',
    restart: 'Rundgang mit Beispieldaten',
    back: 'Zurück zu meinem Verein',
  },
  avatar: {
    title: 'Profilbild',
    change: 'Profilbild ändern',
    add: 'Profilbild hinzufügen',
    remove: 'Entfernen',
    saving: 'Wird gespeichert …',
    cropTitle: 'Bildausschnitt wählen',
    cropHint: 'Ziehen verschiebt den Ausschnitt, Regler oder Mausrad zoomen.',
    zoom: 'Zoom',
    apply: 'Übernehmen',
    hint: 'Freiwillig. Sichtbar neben deinem Namen in jedem deiner Vereine; wird beim Hochladen auf 256 × 256 Pixel verkleinert.',
    refused: {
      not_jpeg: 'Bitte ein Bild im JPEG-Format senden.',
      exif: 'Das Bild enthält noch Metadaten (EXIF).',
      too_large: 'Das Bild ist zu groß.',
      not_square: 'Das Bild muss quadratisch sein.',
      size: 'Das Bild muss zwischen 64 und 512 Pixel groß sein.',
    },
  },
  /** The club's logo (club admin, Verwaltung → Verein; core/logo.ts). */
  logo: {
    title: 'Vereinslogo',
    add: 'Logo hochladen',
    change: 'Logo ersetzen',
    remove: 'Logo entfernen',
    saved: 'Logo gespeichert.',
    removed: 'Logo entfernt.',
    hint: 'Erscheint bei der Auswahl des Vereins und oben in der Leiste – im Browser und in der App. Am besten ein Bild mit durchsichtigem Hintergrund. Vor dem Speichern wählst du den Ausschnitt; danach wird es auf 256 × 256 Pixel verkleinert, Metadaten werden entfernt.',
    alt: (club: string) => `Logo ${club}`,
    cropTitle: 'Ausschnitt des Logos',
    cropHint: 'Anfangs passt das ganze Bild hinein. Zum Zuschneiden zoomen (Regler, Mausrad, zwei Finger) und ziehen.',
    refused: {
      not_png: 'Bitte ein Bild im PNG-Format senden.',
      metadata: 'Das Bild enthält noch Metadaten.',
      too_large: 'Das Bild ist zu groß.',
      size: 'Das Bild muss zwischen 32 und 512 Pixel groß sein.',
    },
  },
  /**
   * Notices of the technicians on one aircraft (notices.ts, 0022). The two
   * lower levels say outright that the aircraft stays airworthy — a pilot who
   * reads „Kurskreisel INOP“ must not have to guess whether he may fly.
   */
  notices: {
    title: 'Hinweise der Technik',
    airworthy: 'Flugzeug flugtauglich',
    grounded: 'Sicherheitsrelevant – Flugzeug gesperrt',
    levelHint: {
      info: 'Zur Kenntnis, z. B. eine laufende Fehlersuche. Das Flugzeug bleibt flugtauglich.',
      einschraenkung: 'Ein Gerät oder eine Funktion steht nicht zur Verfügung, z. B. „Kurskreisel INOP“. Das Flugzeug bleibt flugtauglich.',
      gesperrt: 'Sicherheitsrelevant: das Flugzeug ist gesperrt. Die Buchung sperrt ihr wie bisher in Vereinsflieger.',
    },
    add: 'Hinweis hinzufügen',
    edit: 'Hinweis bearbeiten',
    resolve: 'Aufheben',
    resolveConfirm: 'Hinweis aufheben? Er verschwindet für alle und bleibt im Protokoll.',
    resolved: 'Hinweis aufgehoben.',
    saved: 'Hinweis gespeichert.',
    levelLabel: 'Art',
    titleLabel: 'Kurz',
    titlePlaceholder: 'z. B. Kurskreisel INOP',
    bodyLabel: 'Näheres (optional)',
    validUntilLabel: 'Gilt bis (optional)',
    validUntilHint: 'Nach diesem Tag verschwindet der Hinweis von selbst. Leer: bis jemand ihn aufhebt.',
    validUntil: (day: string) => `gilt bis ${day}`,
    acknowledge: 'Gelesen',
    acknowledgedToast: 'Als gelesen vermerkt.',
    resolveAsk: 'Wirklich aufheben?',
    acknowledged: (when: string) => `Gelesen am ${when}`,
    readBy: (count: number) => (count === 1 ? '1 × gelesen' : `${count} × gelesen`),
    readByNobody: 'Noch von niemandem gelesen',
    readers: 'Gelesen von',
    by: (name: string, when: string) => `${name}, ${when}`,
    history: 'Aufgehobene Hinweise',
    historyEmpty: 'Noch kein aufgehobener Hinweis.',
    resolvedBy: (name: string, when: string) => `aufgehoben von ${name}, ${when}`,
    count: (n: number) => (n === 1 ? '1 Hinweis' : `${n} Hinweise`),
    reportWarning: (title: string) => `Flugzeug gesperrt: ${title}`,
    reportWarningHint: 'Melden geht trotzdem – ein neu entdeckter Schaden soll nie verloren gehen.',
    groundingTitle: 'Flugzeuge sperren',
    groundingLabel: 'Technik darf ein Flugzeug sicherheitsrelevant sperren',
    groundingHint: 'Eingeschaltet kann ein Hinweis der Technik zusätzlich „Sicherheitsrelevant – Flugzeug gesperrt“ sein. Außencheck zeigt die Sperre überall deutlich an und lässt Meldungen weiter zu; die Buchung sperrt ihr wie bisher in Vereinsflieger.',
    groundingOff: 'In diesem Verein ist das Sperren von Flugzeugen nicht eingeschaltet (Verwaltung → Einstellungen).',
    invalidLevel: 'Unbekannte Art: erlaubt sind info, einschraenkung und gesperrt.',
    invalidDay: 'Bitte einen Tag im Format JJJJ-MM-TT angeben.',
    alreadyResolved: 'Dieser Hinweis ist bereits aufgehoben.',
  },
  errors: {
    offline: 'Keine Verbindung. Bitte später erneut versuchen.',
    generic: 'Das hat nicht geklappt. Bitte erneut versuchen.',
    notFound: 'Nicht gefunden.',
  },
  footer: {
    version: (build: string, commit: string, builtAt: string) =>
      `Version ${build} · ${commit} · ${builtAt}`,
  },
  /** The web's "a newer build is live" band (apps/web/src/lib/versionCheck.ts). */
  update: {
    available: 'Neue Version verfügbar',
    reload: 'Neu laden',
    /** A part loaded on demand is gone from the server: the page is older than the deployment. */
    partMissing: 'Dieser Teil der Seite ließ sich nicht laden – vermutlich gibt es eine neue Version.',
  },
} as const;

export type Texts = typeof de;
